// ./client/src/components/Messages/MessageLog.js
import React, { useState, useEffect } from "react";
import { useAppContext } from "../../context/appContext";
import { Display, Display1, Input } from "../workspace";
import htmlToText from "../../utils/htmlToText";

const dateFormat = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

function SmsLog({ jobId }) {
  const { isLoading, authFetch, lists } = useAppContext();
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);

  const showFilters = !jobId;

  const now = new Date();
  const initialFilters = {
    search: "",
    status: "all",
    channel: "all",
    beginTime: "",
    endTime: now.toLocaleString(),
  };
  const [filters, setFilters] = useState(initialFilters);

  const getDate = (date) => new Date(date).toLocaleString("en-US", dateFormat);

  const loadMessages = async () => {
    const { data } = await authFetch(
      jobId ? `/messages/job/${jobId}` : "/messages/all"
    );
    setMessages(data.messages || []);
    setSelected(null);
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const filterResults = (newFilters) => {
    if (!showFilters || !messages) {
      return messages;
    }
    return messages.filter((record) => {
      const {
        status,
        channel,
        to_contact,
        from_contact,
        subject,
        body,
        error,
        message_id,
        job_id,
        created_at,
      } = record;
      // build lowercase variables for an easy case agnostic search
      const procFilter = newFilters.search?.toLowerCase();
      const statusSearch = status?.toLowerCase();
      const channelSearch = channel?.toLowerCase();
      const toSearch = to_contact?.toLowerCase();
      const fromSearch = from_contact?.toLowerCase();
      const subjectSearch = subject?.toLowerCase();
      const bodySearch = htmlToText(body)?.toLowerCase();
      const errorSearch = error?.toLowerCase();
      const messageIdSearch = message_id?.toLowerCase();
      const jobNumberSearch = getJobNumber(job_id)?.toLowerCase();
      const createdDate = new Date(created_at);
      const beginDate = newFilters.beginTime
        ? new Date(newFilters.beginTime)
        : new Date(0);
      const endDate = new Date(newFilters.endTime);

      // check the search input text for matches
      const searchMatch =
        !procFilter ||
        statusSearch?.includes(procFilter) ||
        channelSearch?.includes(procFilter) ||
        toSearch?.includes(procFilter) ||
        fromSearch?.includes(procFilter) ||
        subjectSearch?.includes(procFilter) ||
        bodySearch?.includes(procFilter) ||
        errorSearch?.includes(procFilter) ||
        messageIdSearch?.includes(procFilter) ||
        jobNumberSearch?.includes(procFilter);

      // check the status select
      const statusMatch =
        newFilters.status === "all" || status === newFilters.status;
      // check the channel select
      const channelMatch =
        newFilters.channel === "all" || channel === newFilters.channel;
      // check the time window
      const timeMatch =
        beginDate.getTime() < createdDate.getTime() &&
        endDate.getTime() >= createdDate.getTime();

      return searchMatch && statusMatch && channelMatch && timeMatch;
    });
  };

  const handleInput = (e) => {
    const name = e.target.name;
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    setFilters({
      ...filters,
      [name]: value,
    });
  };

  useEffect(() => {
    loadMessages();
  }, []);

  const getJobNumber = (jobId) => {
    return lists.allJobs?.filter(
      (job) => String(job.id) === String(jobId)
    )[0]?.job_number;
  };

  const statusColor = (status) =>
    status === "Delivered" || status === "Sent" ? "text-green" : "text-red";

  return (
    <div className="group whole">
      <form className="form" onSubmit={(e) => e.preventDefault()}>
        <h3>Emails and Text Messages</h3>
        {showFilters && (
          <div className="form-center">
            <button className="btn" type="button" onClick={loadMessages} disabled={isLoading}>
              Refresh Log
            </button>
            <button className="btn red" type="button" onClick={resetFilters} disabled={isLoading}>
              Clear All
            </button>
            <div className="three-fourths">
              <Input
                type="text"
                name="search"
                value={filters.search}
                handleChange={handleInput}
              />
            </div>
            <div className="one-fourth flex-parent form-row">
              <label className="form-label" htmlFor="status-select">
                Status
              </label>
              <select
                id="status-select"
                name="status"
                value={filters.status}
                onChange={handleInput}
                className="form-input"
              >
                <option value="all">All</option>
                <option value="Delivered">Delivered</option>
                <option value="Sent">Sent</option>
                <option value="Failed">Failed</option>
                <option value="Queued">Queued</option>
              </select>
            </div>
            <div className="one-fourth flex-parent form-row">
              <label className="form-label" htmlFor="channel-select">
                Channel
              </label>
              <select
                id="channel-select"
                name="channel"
                value={filters.channel}
                onChange={handleInput}
                className="form-input"
              >
                <option value="all">All</option>
                <option value="sms">SMS</option>
                <option value="email">Email</option>
              </select>
            </div>
            <div className="one-half flex-parent">
              <Input
                type="datetime-local"
                name="beginTime"
                label="Beginning of Search Window"
                value={filters.beginTime}
                handleChange={handleInput}
              />
              <Input
                type="datetime-local"
                name="endTime"
                label="End of Search Window"
                value={filters.endTime}
                handleChange={handleInput}
              />
            </div>
          </div>
        )}
        {!showFilters && (
          <div className="form-center">
            <button className="btn" type="button" onClick={loadMessages} disabled={isLoading}>
              Refresh Log
            </button>
          </div>
        )}
        {/* --- selected message details --- */}
        {selected && (
          <>
            <div className="form-center">
              <div className="one-half">
                <Display name="Sent At" value={getDate(selected.created_at)} />
                <Display name="From" value={selected.from_contact} />
                <Display name="To" value={selected.to_contact} />
              </div>
              <div className="oneone-half">
                <Display name="Message ID" value={selected.message_id} />
                <Display name="Job Number" value={getJobNumber(selected?.job_id)} />
                {selected.subject && <Display name="Subject" value={selected.subject} />}
              </div>
            </div>
            
            <div className="form-center">
              <div className="whole">
                <Display1
                  name="Message Body"
                  value={htmlToText(selected.body)}
                  style={{ maxHeight: "12rem", overflow: "scroll" }}
                />
              </div>
              <div className="whole">
                {selected.error && (
                  <Display1
                    name="Error"
                    value={selected.error || "None"}
                    style={{
                      maxHeight: "12rem",
                      overflow: "scroll",
                      color: selected.error ? "red" : undefined,
                    }}
                  />
                )}
              </div>
            </div>
            <div className="form-center">
              {selected.status_details && (
                <div className="one-half">
                  <Display1
                    name="Status Details"
                    value={JSON.stringify(selected.status_details, null, 2)}
                    style={{
                      maxHeight: "12rem",
                      overflow: "scroll",
                      fontFamily: "monospace",
                    }}
                  />
                </div>
              )}
            </div>
          </>
        )}
        {/* --- messages table --- */}
        <div className="table whole admin-log">
          <table>
            <thead>
              <tr>
                <th style={{ width: 180 }}>Time</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 80 }}>Channel</th>
                <th style={{ width: 160 }}>To</th>
                <th style={{ width: 130 }}>Job Number</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {(showFilters ? filterResults(filters) : messages)?.map((record) => (
                <tr
                  key={record.message_id || record.created_at}
                  className={selected?.message_id === record.message_id ? "selected" : ""}
                  onClick={() => setSelected(record)}
                >
                  <td>{getDate(record.created_at)}</td>
                  <td className={statusColor(record.status)}>{record.status}</td>
                  <td>{record.channel}</td>
                  <td>{record.to_contact}</td>
                  <td>{getJobNumber(record.job_id)}</td>
                  <td>
                    <div className="json-cell">{record.body}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </form>
    </div>
  );
}

export default SmsLog;
